/**
 * Formatting Utilities
 *
 * This file contains functions that format data for display purposes.
 * These are pure functions that transform data without side effects.
 */

import type { StationSector } from './models';
import { getMYTDayShift, shiftDateByDays } from '../timeUtils';

/**
 * Formats a Date or date string to YYYY-MM-DD format
 * @param date - Date object or ISO string
 * @returns Formatted date string "YYYY-MM-DD"
 * @example formatDateToYYYYMMDD(new Date()) => "2026-01-27"
 */
export function formatDateToYYYYMMDD(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date string from YYYY-MM-DD to DD MMM YYYY format
 * @param dateStr - Date string in format "YYYY-MM-DD" or Date object
 * @returns Formatted date string "DD MMM YYYY" or fallback
 * @example formatDisplayDate("2026-02-01") => "01 FEB 2026"
 * @example formatDisplayDate("") => "—"
 */
export function formatDisplayDate(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '—';

  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;

  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();

  return `${day} ${month} ${year}`;
}

/**
 * Formats time from 24h format (0715) to display format (07:15)
 * @param time - Time string in format "HHMM" (must be exactly 4 digits)
 * @returns Formatted time "HH:MM" or fallback if invalid
 * @example formatTime("0715") => "07:15"
 * @example formatTime("2359") => "23:59"
 * @example formatTime("") => "—"
 * @example formatTime("invalid") => "—"
 */
export function formatTime(time: string): string {
  // Validate input: must be exactly 4 digits
  if (!time || time.length !== 4 || !/^\d{4}$/.test(time)) {
    return time || '—';
  }
  return `${time.slice(0, 2)}:${time.slice(2, 4)}`;
}

/**
 * Formats frequency rate for display
 * @param freqRate - Frequency rate string (empty string or "2"-"99")
 * @returns Human-readable frequency rate
 * @example formatFreqRate("") => "Weekly"
 * @example formatFreqRate("2") => "Bi-weekly"
 * @example formatFreqRate("4") => "Every 4 weeks"
 */
export function formatFreqRate(freqRate: string | null | undefined): string {
  if (!freqRate) return 'Weekly';
  if (freqRate === '2') return 'Bi-weekly';
  return `Every ${freqRate} weeks`;
}

/**
 * Gets a descriptive label for the frequency pattern
 * Combines frequency days with frequency rate
 * @param freq - Frequency string (e.g., "1234567" or "135")
 * @param freqRate - Frequency rate (e.g., "", "2", "4")
 * @returns Descriptive label
 * @example getFrequencyLabel("1234567", "") => "Daily"
 * @example getFrequencyLabel("135", "") => "Weekly"
 * @example getFrequencyLabel("1234567", "2") => "Bi-weekly Daily"
 * @example getFrequencyLabel("135", "2") => "Bi-weekly"
 */
export function getFrequencyLabel(freq: string, freqRate: string | null | undefined): string {
  const rate = formatFreqRate(freqRate);

  // If frequency is all 7 days and weekly rate, it's "Daily"
  if (freq === '1234567' && !freqRate) {
    return 'Daily';
  }

  // If frequency is all 7 days but bi-weekly or more, show both
  if (freq === '1234567' && freqRate) {
    return `${rate} Daily`;
  }

  // Otherwise just return the rate
  return rate;
}

/**
 * Returns the appropriate frequency string based on timezone mode.
 * In MYT mode, uses the backend-computed shifted frequency if available.
 * Falls back to UTC frequency for unsaved drafts or missing data.
 */
export function getDisplayFrequency(
  frequency: string,
  frequencyMYT: string | undefined,
  timezone: 'UTC' | 'MYT',
): string {
  return (timezone === 'MYT' && frequencyMYT) ? frequencyMYT : frequency;
}

/**
 * Returns the appropriate date string based on timezone mode.
 * In MYT mode, shifts the date by the day change from STD UTC→MYT conversion.
 * Analogous to getDisplayFrequency for frequency patterns.
 */
export function getDisplayDate(
  utcDate: string | undefined | null,
  stdUTC: string | undefined | null,
  timezone: 'UTC' | 'MYT',
): string {
  if (!utcDate) return '';
  if (timezone !== 'MYT') return utcDate;
  const dayShift = getMYTDayShift(stdUTC);
  if (dayShift === 0) return utcDate;
  return shiftDateByDays(utcDate, dayShift);
}

/**
 * Formats frequency with underscores for missing days
 * Used for visual display in tables
 * @param freq - Frequency string (digits 1-7)
 * @returns Formatted string with underscores for missing days
 * @example formatFrequencyWithUnderscores("1234567") => "1234567"
 * @example formatFrequencyWithUnderscores("1246") => "12_4_6_"
 * @example formatFrequencyWithUnderscores("67") => "______67"
 */
export function formatFrequencyWithUnderscores(freq: string): string {
  const days = ['1', '2', '3', '4', '5', '6', '7'];

  if (!freq) return '_______';

  return days
    .map(day => (freq.includes(day) ? day : '_'))
    .join('');
}

/**
 * Formats message type code to readable format
 * @param code - Message type code ("A", "S", or null)
 * @returns Human-readable message type
 * @example formatMessageType("A") => "ASM"
 * @example formatMessageType("S") => "SSM"
 * @example formatMessageType(null) => "—"
 */
export function formatMessageType(code: string | null | undefined): string {
  if (code === 'A') return 'ASM';
  if (code === 'S') return 'SSM';
  return '—';
}

/**
 * Generates a route string from station sectors
 * @param stations - Array of station sectors
 * @returns Route string with arrows between stations
 * @example getRouteString([{departureStation: "KUL", arrivalStation: "PEN"}, ...])
 *          => "KUL → PEN"
 * @example getRouteString([{departureStation: "KUL", arrivalStation: "SIN"}, {departureStation: "SIN", arrivalStation: "HKG"}])
 *          => "KUL → SIN → HKG"
 */
export function getRouteString(stations: StationSector[]): string {
  if (!stations?.length) return '—';
  const codes = [stations[0].departureStation];

  stations.forEach(s => {
    if (!codes.includes(s.arrivalStation)) {
      codes.push(s.arrivalStation);
    }
  });

  return codes.join(' → ');
}

/**
 * Formats a timestamp to a clean, readable format
 * @param timestamp - Date string or Date object
 * @returns Formatted timestamp "DD MMM YYYY, HH:MM" or fallback
 * @example formatTimestamp("2026-01-27T15:45:23Z") => "27 JAN 2026, 15:45"
 * @example formatTimestamp(null) => "—"
 */
export function formatTimestamp(timestamp: string | Date | null | undefined): string {
  if (!timestamp) return '—';

  const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}
